// Pago de Clientes · consultas (docs/108 B4): CxC por cliente con antigüedad, cartola con saldo corrido,
// vencimientos. (El historial de cobros es ahora la Teneduría de pagos: TeneduriaPagos.tsx.) Todo
// derivado de documentos, NC y aplicaciones: ningún saldo guardado.
import { useCallback, useState } from 'react';
import type { ClienteEncontrado } from '../api/contratos';
import { PanelError } from '../comun/Estados';
import { Grilla, type Columna } from '../comun/Grilla';
import { fecha, pesos } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { BuscadorCliente } from '../ventas/BuscadorCliente';
import {
  NOMBRE_TIPO_DTE,
  useCartola,
  useDocumentosCxc,
  useResumenCxc,
  type DocumentoConSaldo,
  type ResumenCliente,
} from './datos';
import { DescargarCartolaPdf, ImprimirCartolaCliente } from './Impresos';
import { hoyIso } from '../ventas/comercial';

export function SelectorCliente({
  cliente,
  alElegir,
}: {
  cliente: { id: string; razonSocial: string } | null;
  alElegir: (c: { id: string; razonSocial: string } | null) => void;
}) {
  const { empresa } = useSesion();
  const [buscando, setBuscando] = useState(false);
  return (
    <div className="campo">
      <span className="etiqueta">Cliente</span>
      <div className="fila-datos">
        <button
          type="button"
          className="selector-cliente"
          aria-label="Elegir cliente"
          onClick={() => setBuscando(true)}
        >
          {cliente ? cliente.razonSocial : 'Buscar cliente…'}
        </button>
        {cliente && (
          <button
            type="button"
            className="boton-mini"
            aria-label="Quitar cliente"
            onClick={() => alElegir(null)}
          >
            ✕
          </button>
        )}
      </div>
      {buscando && (
        <BuscadorCliente
          empresaId={empresa!.id}
          alElegir={(c: ClienteEncontrado) => {
            alElegir({ id: c.id, razonSocial: c.razon_social });
            setBuscando(false);
          }}
          alCerrar={() => setBuscando(false)}
        />
      )}
    </div>
  );
}

// ================================================================ CxC por cliente

export function CxcResumen({
  alAbrirCartola,
}: {
  alAbrirCartola: (clienteId: string, nombre: string) => void;
}) {
  const { empresa } = useSesion();
  const q = useResumenCxc(empresa!.id);
  const [texto, setTexto] = useState('');
  const filas = (q.data ?? []).filter(
    (r) =>
      !texto ||
      r.cliente.toLowerCase().includes(texto.toLowerCase()) ||
      (r.rut ?? '').includes(texto),
  );
  const total = (k: 'saldo' | 'vencido' | 'saldoAFavor') =>
    filas.reduce((s, r) => s + Number(r[k]), 0);
  const columnas: Columna<ResumenCliente>[] = [
    { id: 'cliente', titulo: 'Cliente', celda: (r) => r.cliente },
    {
      id: 'rut',
      titulo: 'RUT',
      celda: (r) => <span className="mono">{r.rut ?? ''}</span>,
      ancho: '8rem',
    },
    { id: 'docs', titulo: 'Docs', celda: (r) => r.documentos, alinear: 'der', ancho: '4rem' },
    {
      id: 'saldo',
      titulo: 'Saldo',
      celda: (r) => <b>{pesos(r.saldo)}</b>,
      alinear: 'der',
      ancho: '9rem',
    },
    {
      id: 'corriente',
      titulo: 'Por vencer',
      celda: (r) => pesos(r.tramos.corriente),
      alinear: 'der',
      ancho: '8rem',
    },
    {
      id: 't1',
      titulo: '1-30',
      celda: (r) => pesos(r.tramos['1-30']),
      alinear: 'der',
      ancho: '7rem',
    },
    {
      id: 't2',
      titulo: '31-60',
      celda: (r) => pesos(r.tramos['31-60']),
      alinear: 'der',
      ancho: '7rem',
    },
    {
      id: 't3',
      titulo: '61-90',
      celda: (r) => pesos(r.tramos['61-90']),
      alinear: 'der',
      ancho: '7rem',
    },
    {
      id: 't4',
      titulo: '91+',
      celda: (r) => (
        <span className={Number(r.tramos['91+']) ? 'tono-malo' : ''}>{pesos(r.tramos['91+'])}</span>
      ),
      alinear: 'der',
      ancho: '7rem',
    },
    {
      id: 'favor',
      titulo: 'A favor',
      celda: (r) => (Number(r.saldoAFavor) ? pesos(r.saldoAFavor) : ''),
      alinear: 'der',
      ancho: '7rem',
    },
    { id: 'neto', titulo: 'Neto', celda: (r) => pesos(r.neto), alinear: 'der', ancho: '8rem' },
  ];
  return (
    <div className="pantalla consulta" data-pantalla="cxc">
      <div className="barra-filtros">
        <label className="campo">
          <span className="etiqueta">Buscar</span>
          <input
            aria-label="Buscar cliente en CxC"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
        </label>
        <span className="espaciador" />
        <span>
          Saldo <b>{pesos(String(total('saldo')))}</b> · vencido{' '}
          <b className="tono-malo">{pesos(String(total('vencido')))}</b> · a favor{' '}
          <b>{pesos(String(total('saldoAFavor')))}</b>
        </span>
      </div>
      {q.isError && <PanelError error={q.error} />}
      <Grilla
        etiqueta="Cuentas por cobrar por cliente"
        columnas={columnas}
        filas={filas}
        clave={(r) => r.clienteEntidadId}
        cargando={q.isLoading}
        vacio="No hay cuentas por cobrar abiertas"
        alAbrir={(r) => alAbrirCartola(r.clienteEntidadId, r.cliente)}
      />
    </div>
  );
}

// ================================================================ cartola

export function CartolaCliente({
  clienteInicial,
  alCobrar,
}: {
  clienteInicial?: { id: string; razonSocial: string } | undefined;
  alCobrar?: (c: { id: string; razonSocial: string }) => void;
}) {
  const { empresa } = useSesion();
  const [cliente, setCliente] = useState<{ id: string; razonSocial: string } | null>(
    clienteInicial ?? null,
  );
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [imprimir, setImprimir] = useState(false);
  const [pdf, setPdf] = useState(false);
  const q = useCartola(empresa!.id, cliente?.id ?? null, {
    ...(desde ? { desde } : {}),
    ...(hasta ? { hasta } : {}),
  });
  const terminar = useCallback(() => setImprimir(false), []);
  const terminarPdf = useCallback(() => setPdf(false), []);
  return (
    <div className="pantalla consulta" data-pantalla="cartola">
      <div className="barra-filtros">
        <SelectorCliente cliente={cliente} alElegir={setCliente} />
        <label className="campo">
          <span className="etiqueta">Desde</span>
          <input
            type="date"
            aria-label="Desde"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
          />
        </label>
        <label className="campo">
          <span className="etiqueta">Hasta</span>
          <input
            type="date"
            aria-label="Hasta"
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
          />
        </label>
        <span className="espaciador" />
        {q.data && (
          <span>
            Saldo inicial <b>{pesos(q.data.saldoInicial)}</b> · saldo final{' '}
            <b>{pesos(q.data.saldoFinal)}</b>
            {Number(q.data.saldoAFavor) > 0 && (
              <>
                {' '}
                · a favor sin aplicar <b>{pesos(q.data.saldoAFavor)}</b>
              </>
            )}
          </span>
        )}
        {q.data && (
          <button type="button" onClick={() => setImprimir(true)}>
            Imprimir cartola
          </button>
        )}
        {q.data && (
          <button type="button" disabled={pdf} onClick={() => setPdf(true)}>
            {pdf ? 'Preparando PDF…' : 'Descargar PDF'}
          </button>
        )}
        {cliente && alCobrar && (
          <button type="button" className="primario" onClick={() => alCobrar(cliente)}>
            Cobrar
          </button>
        )}
      </div>
      {q.isError && <PanelError error={q.error} />}
      {!cliente ? (
        <p className="apagado vacio-centrado">Elige un cliente para ver su cuenta corriente.</p>
      ) : (
        <table className="grilla compacta grilla-cuenta" aria-label="Cartola del cliente">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>TD</th>
              <th>Número</th>
              <th>NV</th>
              <th>Vence</th>
              <th>Detalle</th>
              <th className="der">Cargo</th>
              <th className="der">Abono</th>
              <th className="der">Saldo</th>
            </tr>
          </thead>
          <tbody>
            {(q.data?.movimientos ?? []).map((m, i) => (
              <tr key={i} data-tipo={m.tipo}>
                <td>{fecha(m.fecha)}</td>
                <td className="mono">{m.td ?? ''}</td>
                <td className="mono">{m.numero ?? ''}</td>
                <td className="mono">{m.nv ?? ''}</td>
                <td>{m.vence ? fecha(m.vence) : ''}</td>
                <td>{m.detalle ?? m.descripcion}</td>
                <td className="der">{Number(m.cargo) ? pesos(m.cargo) : ''}</td>
                <td className="der">{Number(m.abono) ? pesos(m.abono) : ''}</td>
                <td className="der">
                  <b>{pesos(m.saldo)}</b>
                </td>
              </tr>
            ))}
            {q.isSuccess && !q.data.movimientos.length && (
              <tr>
                <td colSpan={9} className="apagado">
                  Sin movimientos en el período
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
      {cliente && !!q.data?.porRevisar?.length && (
        <p className="aviso-linea tono-aviso" data-por-revisar>
          Por revisar:{' '}
          {q.data.porRevisar
            .map(
              (d) =>
                `${d.td} ${d.numero} (${d.nv ?? '—'}) se anuló entera con nota de crédito después de pagada y quedó con ${pesos(d.excedente)} pagados`,
            )
            .join('; ')}
          . No se descuenta solo de otras facturas: revisa si el pago se registró dos veces (se
          anula el recibo en Teneduría de pagos) o si se le devolvió la plata al cliente.
        </p>
      )}
      {cliente && !!q.data?.pendientes?.length && (
        <>
          <h3 className="titulo-seccion">Documentos con saldo</h3>
          <table className="grilla compacta grilla-cuenta" aria-label="Documentos con saldo">
            <thead>
              <tr>
                <th>TD</th>
                <th>Número</th>
                <th>NV</th>
                <th>Emisión</th>
                <th>Vence</th>
                <th className="der">Días venc.</th>
                <th className="der">Valor doc.</th>
                <th className="der">Saldo</th>
                <th>Origen</th>
              </tr>
            </thead>
            <tbody>
              {q.data.pendientes.map((d, i) => (
                <tr key={i} className={d.diasVencido > 0 ? 'vencido' : ''}>
                  <td className="mono">{d.td}</td>
                  <td className="mono">{d.numero}</td>
                  <td className="mono">{d.nv ?? ''}</td>
                  <td>{fecha(d.emision)}</td>
                  <td>{d.vence ? fecha(d.vence) : ''}</td>
                  <td className={`der ${d.diasVencido > 0 ? 'tono-malo' : ''}`}>
                    {d.diasVencido || ''}
                  </td>
                  <td className="der">{pesos(d.valor)}</td>
                  <td className="der">
                    <b>{pesos(d.saldo)}</b>
                  </td>
                  <td className="apagado">{d.heredado ? 'Heredado de Random' : 'ERP'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      {imprimir && cliente && (
        <ImprimirCartolaCliente
          clienteId={cliente.id}
          nombre={cliente.razonSocial}
          desde={desde || undefined}
          hasta={hasta || undefined}
          alTerminar={terminar}
        />
      )}
      {pdf && cliente && (
        <DescargarCartolaPdf
          clienteId={cliente.id}
          nombre={cliente.razonSocial}
          desde={desde || undefined}
          hasta={hasta || undefined}
          alTerminar={terminarPdf}
        />
      )}
    </div>
  );
}

// ================================================================ vencimientos

export function Vencimientos() {
  const { empresa } = useSesion();
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState(hoyIso());
  const [cliente, setCliente] = useState<{ id: string; razonSocial: string } | null>(null);
  const q = useDocumentosCxc(empresa!.id, {
    abiertos: true,
    clienteEntidadId: cliente?.id,
    ...(desde ? { vencenDesde: desde } : {}),
    ...(hasta ? { vencenHasta: hasta } : {}),
  });
  const total = (q.data ?? []).reduce((s, d) => s + Number(d.saldo), 0);
  const columnas: Columna<DocumentoConSaldo>[] = [
    { id: 'vence', titulo: 'Vence', celda: (d) => fecha(d.fechaVencimiento), ancho: '7rem' },
    {
      id: 'dias',
      titulo: 'Días',
      celda: (d) => (
        <span className={d.diasVencido > 0 ? 'tono-malo' : ''}>{d.diasVencido || '—'}</span>
      ),
      alinear: 'der',
      ancho: '4rem',
    },
    { id: 'cliente', titulo: 'Cliente', celda: (d) => d.clienteRazonSocial },
    {
      id: 'doc',
      titulo: 'Documento',
      celda: (d) => `${NOMBRE_TIPO_DTE[d.tipoDte] ?? d.tipoDte} N° ${d.folio ?? '—'}`,
      ancho: '11rem',
    },
    {
      id: 'nv',
      titulo: 'NV',
      celda: (d) => <span className="mono">{d.notaNumeroVisible}</span>,
      ancho: '9rem',
    },
    {
      id: 'total',
      titulo: 'Total',
      celda: (d) => pesos(d.montoTotal),
      alinear: 'der',
      ancho: '8rem',
    },
    {
      id: 'saldo',
      titulo: 'Saldo',
      celda: (d) => <b>{pesos(d.saldo)}</b>,
      alinear: 'der',
      ancho: '8rem',
    },
  ];
  return (
    <div className="pantalla consulta" data-pantalla="vencimientos">
      <div className="barra-filtros">
        <SelectorCliente cliente={cliente} alElegir={setCliente} />
        <label className="campo">
          <span className="etiqueta">Vencen desde</span>
          <input
            type="date"
            aria-label="Vencen desde"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
          />
        </label>
        <label className="campo">
          <span className="etiqueta">Vencen hasta</span>
          <input
            type="date"
            aria-label="Vencen hasta"
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
          />
        </label>
        <span className="espaciador" />
        <span>
          {q.data?.length ?? 0} documentos · <b>{pesos(String(total))}</b>
        </span>
      </div>
      {q.isError && <PanelError error={q.error} />}
      <Grilla
        etiqueta="Vencimientos"
        columnas={columnas}
        filas={q.data ?? []}
        clave={(d) => d.documentoId}
        cargando={q.isLoading}
        vacio="Sin documentos que venzan en el período"
      />
    </div>
  );
}
